import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { store } from "../app/store";
import { _get, _post } from "../api/apiClient";
import { HttpStatusCode } from "axios";
import { CustomError } from "../api/CustomError";
import { CustomSuccess } from "../api/CustomSuccess";
import { fetchCurrentCachedState, getSelectedCardName } from "../utils/Utils";
import {
  setLoaderFalse,
  setLoaderTrue,
  setPerformaLoading,
} from "./loaderSlice";
import { current } from "immer";
import {
  getGraphObjectsFromInitialFilter,
  initialFilterDataSlice,
} from "./initialFilterDataSlice";
import {
  setApplyButtonClicked,
  setEnableApplyButton,
} from "./selectedFilterSlice";
import { API_STATUS } from "../rtk-query/features/sliceUtil";

export const SORT_BY = {
  alphabetical: "alphabetical",
  numerical: "numerical",
};

export const SORT_ORDER = {
  ascending: "ascending",
  descending: "descending",
};

export const FILTER_KEYS = {
  balance: "balance",
  oppotype: "oppotype",
  products: "products",
  product_with_provider: "product_with_provider",
  consumer: "consumer",
  persona: "persona",
  zonename: "zonename",
  consumertype: "consumertype",
  deposit_type: "deposit_type",
  depositType_with_provider: "product_with_provider",
  providers: "providers",
  category: "category",
  category_with_merchant: "category_with_merchant",
  merchant: "merchant",
};

export const CLUSTER_OBJECTS = {
  opportunities: {
    legendsKey: FILTER_KEYS?.products,
    zonesKey: FILTER_KEYS.zonename,
  },
  secondary_consumer: {
    // legendsKey: FILTER_KEYS?.consumertype,
    legendsKey: FILTER_KEYS?.deposit_type,
    zonesKey: FILTER_KEYS.zonename,
  },
  ghost_consumer: {
    legendsKey: FILTER_KEYS?.products,
    zonesKey: FILTER_KEYS.zonename,
  },
  top_merchant: {
    legendsKey: FILTER_KEYS?.category,
    zonesKey: FILTER_KEYS.zonename,
  },
};

const initialState = {
  graphData: null,
  detailData: null,
  filterData: null,
  initialFilterData: {
    data: null,
    isLoading: true,
  },
  selectedFilterData: {},
  contextualFilterObj: null,
  FILTERS: {
    opportunities: [
      {
        key: FILTER_KEYS.balance,
        filterName: "Checking Account Balance",
      },
      {
        key: FILTER_KEYS.oppotype,
        filterName: "Opportunity Types",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
      },
      {
        key: FILTER_KEYS.products,
        filterName: "Products",
        postFix: "Providers",
        subFilterName: FILTER_KEYS.product_with_provider,
        subFilterKey: FILTER_KEYS.product_with_provider,
        subFilterInternalKey: "product",
        subFilterDisplayKey: "name",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
        subFilterSortBy: SORT_BY.alphabetical,
        subFilterSortOrder: SORT_ORDER.ascending,
        extendMenu: false,
      },
      {
        key: FILTER_KEYS.consumer,
        filterName: "Consumers",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
      },
      {
        key: FILTER_KEYS.persona,
        filterName: "Personas",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
      },
      {
        key: FILTER_KEYS.zonename,
        filterName: "Zones",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
      },
    ],
    secondary_consumer: [
      {
        key: FILTER_KEYS.balance,
        filterName: "Checking Account Balance",
      },
      {
        key: FILTER_KEYS.deposit_type,
        filterName: "External Transfers",
        postFix: "Providers",
        subFilterName: FILTER_KEYS.depositType_with_provider,
        subFilterKey: FILTER_KEYS.depositType_with_provider,
        subFilterInternalKey: "deposit",
        subFilterDisplayKey: "name",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
        subFilterSortBy: SORT_BY.alphabetical,
        subFilterSortOrder: SORT_ORDER.ascending,
        extendMenu: false,
      },
      {
        key: FILTER_KEYS.providers,
        filterName: "Financial Institutions",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
      },
      {
        key: FILTER_KEYS.consumertype,
        filterName: "Consumers",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
      },
      {
        key: FILTER_KEYS.persona,
        filterName: "Personas",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
      },
      {
        key: FILTER_KEYS.zonename,
        filterName: "Zones",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
      },
    ],
    ghost_consumer: [
      {
        key: FILTER_KEYS.balance,
        filterName: "Checking Account Balance",
      },
      {
        key: FILTER_KEYS.products,
        filterName: "Products",
        postFix: "Providers",
        subFilterName: FILTER_KEYS.product_with_provider,
        subFilterKey: FILTER_KEYS.product_with_provider,
        subFilterInternalKey: "product",
        subFilterDisplayKey: "name",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
        subFilterSortBy: SORT_BY.alphabetical,
        subFilterSortOrder: SORT_ORDER.ascending,
        extendMenu: false,
      },
      {
        key: FILTER_KEYS.consumer,
        filterName: "Consumers",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
      },
      {
        key: FILTER_KEYS.persona,
        filterName: "Personas",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
      },
      {
        key: FILTER_KEYS.zonename,
        filterName: "Zones",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
      },
    ],
    top_merchant: [
      {
        key: FILTER_KEYS.balance,
        filterName: "Expenditure Amount",
      },
      {
        key: FILTER_KEYS.category,
        filterName: "Category",
        postFix: "Merchants",
        subFilterName: FILTER_KEYS.category_with_merchant,
        subFilterKey: FILTER_KEYS.category_with_merchant,
        subFilterInternalKey: "category",
        subFilterDisplayKey: "name",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
        subFilterSortBy: SORT_BY.numerical,
        subFilterSortOrder: SORT_ORDER.descending,
        extendMenu: false,
      },
      {
        key: FILTER_KEYS.consumer,
        filterName: "Consumers",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
      },
      {
        key: FILTER_KEYS.merchant,
        filterName: "Merchants",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
      },
      {
        key: FILTER_KEYS.persona,
        filterName: "Personas",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
      },
      {
        key: FILTER_KEYS.zonename,
        filterName: "Zones",
        sortBy: SORT_BY.alphabetical,
        sortOrder: SORT_ORDER.ascending,
      },
    ],
  },
  selectedZones: null,
  selectedFilterObject: null,
  isOnlyPredictive: false,
};

const _orgClientId = () => {
  const state = store.getState();
  return state?.orgReducer?.selectedFI?.orgClientId;
};

const _hid = () => {
  const state = store.getState();
  return state?.orgReducer?.healthAnalysisID.uid;
};

const _FILTERS = () => {
  const state = store.getState();
  return state?.clusterReducer?.FILTERS;
};

const _contextualFilterObj = () => {
  const state = store.getState();
  return state?.clusterReducer?.contextualFilterObj;
};

const _userMailId = () => {
  const state = store.getState();
  return state?.authReducer?.userData?.userName;
};

export const getOnlyIdsForAPI = () => {
  const state = store.getState();
  const selectedFilterData = state?.clusterReducer?.selectedFilterData;
  const result = {};
  for (const [key, value] of Object.entries(selectedFilterData)) {
    if (Array.isArray(value)) {
      result[key] = value.map((item) => item.id);
    } else if (typeof value === "object" && value !== null) {
      result[key] = value; // keep balance or other object fields as-is
    }
  }
  const cleanedResult = Object.fromEntries(
    Object.entries(result).filter(
      ([k, v]) => !Array.isArray(v) || v.length > 0,
    ),
  );
  return cleanedResult;
};

export const transformFilterData = (inputArray) => {
  const output = {};
  inputArray.forEach((item) => {
    if (!item || typeof item !== "object") return;
    const cardName = getSelectedCardName();
    Object.entries(item).forEach(([key, value]) => {
      const config = _FILTERS()?.[cardName?.key]?.find(
        (item) => item.key === key,
      );
      if (key === FILTER_KEYS.balance) {
        output[key] = {
          ...config,
          data: value[0],
        };
      } else {
        if (config?.subFilterKey != null) {
          output[key] = {
            ...config,
            data: value?.map((i) => ({
              ...i,
              childOf: config?.key,
              postFix: config?.postFix,
              subFilterKey: config?.subFilterKey,
              subFilterName: config?.subFilterName,
              subFilterInternalKey: config?.subFilterInternalKey,
              extendMenu: false,
              subFilterSortBy: config?.subFilterSortBy,
              subFilterSortOrder: config?.subFilterSortOrder,
              [config?.subFilterKey]: i[config?.subFilterKey]?.map((j) => ({
                ...j,
                childOf: i?.name,
                rootFilter: config?.key,
                subFilterKey: config?.subFilterKey,
                subFilterDisplayKey: config?.subFilterDisplayKey,
              })),
            })),
          };
        } else {
          output[key] = {
            ...config,
            data: value?.map((i) => ({
              ...i,
              childOf: config?.key,
            })),
          };
        }
      }
    });
  });
  return output;
};

export const transformGraphData = async (data) => {
  if (!data?.[0]) return null;

  const { graphItems, zoneItems } = await getGraphObjectsFromInitialFilter();

  const graphMap = new Map(
    graphItems?.data?.map(({ id, name, color_code }) => [
      id,
      { id, name, color: color_code },
    ]),
  );

  const zoneMap = new Map(zoneItems?.data?.map((item) => [item?.id, item]));

  const transformedZones =
    data[0].zone?.map((zone) => {
      const updatedZoneData =
        zone.data?.map((item) => {
          return {
            ...item,
            ...(graphMap.get(item?.id) || {}),
            value: item?.count,
          };
        }) || [];

      return {
        ...zone,
        ...(zoneMap.get(zone?.zone_id) || {}),
        data: updatedZoneData,
      };
    }) || [];

  return {
    ...data[0],
    zone: transformedZones,
  };
};

export const fetchFilterApiThunk = createAsyncThunk(
  "fetch_filter_api_thunk",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Fetched Graph data successfully.",
        errorMessage: "Failed to fetch Graph data!",
      };

      const response = await _post(
        `bank/${_orgClientId()}/drilldown/${_hid()}/${getSelectedCardName()?.key}/filter`,
        {
          filter: {},
          requid: _hid(),
          gte: "",
          lte: "",
          duration: "",
        },
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const { data, error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      // thunkAPI.dispatch(setLoaderFalse());
      return {
        data,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (error) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const fetchGraphApiThunk = createAsyncThunk(
  "fetch_graph_api_thunk",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Fetched Graph data successfully.",
        errorMessage: "Failed to fetch Graph data!",
      };

      let payload = {
        filter: getOnlyIdsForAPI(),
        requid: _hid(),
        gte: "",
        lte: "",
        duration: "",
        Is_context: _contextualFilterObj() != null,
      };

      const response = await _post(
        `bank/${_orgClientId()}/drilldown/${_hid()}/${getSelectedCardName()?.key}/graph`,
        { ...payload },
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const { data, error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      const graphData = await transformGraphData(data);

      thunkAPI.dispatch(setEnableApplyButton(false));
      thunkAPI.dispatch(setApplyButtonClicked(false));
      thunkAPI.dispatch(setLoaderFalse());
      return {
        data: graphData,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (error) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const fetchDetailApiThunk = createAsyncThunk(
  "fetch_detail_api_thunk",
  async ({ paginationObj }, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Fetched detail data successfully.",
        errorMessage: "Failed to fetch detail data!",
      };

      const _pageNumber = paginationObj?.currentPage
        ? `offset=${paginationObj?.currentPage}`
        : "";
      const _pageSize =
        paginationObj?.limit > 0 ? `&limit=${paginationObj?.limit}` : "";

      const _status = paginationObj?.selectedFilter?.code
        ? `&Status=${paginationObj?.selectedFilter?.code}`
        : "";
      const _sortBy = paginationObj?.sortObj?.sortOrder
        ? `&SortBy=${paginationObj?.sortObj?.sortOrder}`
        : "";
      const _sortField = paginationObj?.sortObj?.sortField
        ? `&SortField=${paginationObj?.sortObj?.sortField}`
        : "";
      const _searchBy = paginationObj?.searchData
        ? `&searchBy=${paginationObj?.searchData}`
        : "";

      let payload = {
        filter: getOnlyIdsForAPI(),
        requid: _hid(),
        gte: "",
        lte: "",
        duration: "",
      };

      const response = await _post(
        `bank/${_orgClientId()}/drilldown/${_hid()}/${getSelectedCardName()?.key}/detail?${_pageNumber}${_pageSize}`,
        { ...payload },
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const { data, error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      thunkAPI.dispatch(setEnableApplyButton(false));
      thunkAPI.dispatch(setApplyButtonClicked(false));
      thunkAPI.dispatch(setLoaderFalse());
      return {
        data,
        paginationObj,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (error) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const exportFilteredDrilldownDataThunk = createAsyncThunk(
  "export_filtered_drilldown_data",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(setPerformaLoading(true));
      const config = {
        successMessage: "Fetched Graph data successfully.",
        errorMessage: "Failed to fetch Graph data!",
      };

      let payload = {
        // filter: getOnlyIdsForAPI(),
        filter: {
          ...getOnlyIdsForAPI(),
          username: _userMailId(),
        },
        requid: _hid(),
        gte: "",
        lte: "",
        duration: "",
      };
      const response = await _post(
        `bank/${_orgClientId()}/drilldown/${_hid()}/${getSelectedCardName()?.key}/export?offset=0&limit=0`,
        { ...payload },
      );
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      const { data, error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });
      const fileUrl = data;
      const fileName = getSelectedCardName()?.name;
      const fileResponse = await window.fetch(fileUrl);
      const blob = await fileResponse.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName} export data.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      thunkAPI.dispatch(setPerformaLoading(false));
      return {
        data,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (error) {
      thunkAPI.dispatch(setPerformaLoading(false));
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const clusterSlice = createSlice({
  name: "cluster_slice",
  initialState,
  reducers: {
    setSelectedFilterData: (state, action) => {
      let selectedFilterData = action?.payload?.data;
      let selectedFilterIds = [];
      if (action.payload.storeInKey !== FILTER_KEYS?.balance) {
        selectedFilterIds = selectedFilterData?.map((item) => item.id);
      }
      state.selectedFilterData = {
        ...state.selectedFilterData,
        [action.payload.storeInKey]: selectedFilterData,
      };
    },
    setReplaceAllFilterData: (state, action) => {
      const _data = {
        ...action?.payload?.data,
        filterId: action?.payload?.filterId,
      };
      state.selectedFilterData = _data;
    },
    setContextualFilterObj: (state, action) => {
      state.contextualFilterObj = action?.payload;
    },
    updateSortParameters: (state, action) => {
      const {
        filter,
        sortOrder,
        sortBy,
        subFilterSortOrder,
        subFilterSortBy,
        isSubFilterSortEvent,
      } = action.payload;
      const existingArray = current(state.FILTERS) || [];
      const updatedArray = isSubFilterSortEvent
        ? existingArray?.[getSelectedCardName()?.key].map((item) =>
            item.key === filter
              ? { ...item, subFilterSortOrder, subFilterSortBy }
              : { ...item },
          )
        : existingArray?.[getSelectedCardName()?.key].map((item) =>
            item.key === filter ? { ...item, sortOrder, sortBy } : { ...item },
          );
      state.FILTERS = {
        ...state.FILTERS,
        [getSelectedCardName()?.key]: updatedArray,
      };
    },
    updateExpendedMenu: (state, action) => {
      const { storeInKey, selectedCheckboxId, extendMenu } = action.payload;
      const existingArray = current(state.FILTERS) || [];
      const updatedArray = existingArray?.[getSelectedCardName()?.key].map(
        (item) =>
          item.subFilterKey != null && item.key === storeInKey
            ? { ...item, extendMenu, expendMenuId: selectedCheckboxId }
            : { ...item },
      );
      state.FILTERS = {
        ...state.FILTERS,
        [getSelectedCardName()?.key]: updatedArray,
      };
    },
    resetSelectedFilterData: (state) => {
      state.selectedFilterData = {};
      state.contextualFilterObj = null;
    },
    resetClusterReducerState: (state) => {
      return initialState;
    },
    setSelectedZones: (state, action) => {
      state.selectedZones = action.payload;
    },
    setSelectedFilterObject: (state, action) => {
      state.selectedFilterObject = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchFilterApiThunk.pending, (_state, _action) => {
        _state.initialFilterData = {
          isLoading: true,
        };
      })
      .addCase(fetchFilterApiThunk.fulfilled, (_state, _action) => {
        _state.initialFilterData = {
          data: transformFilterData(_action?.payload?.data),
          isLoading: false,
        };
      })
      .addCase(fetchFilterApiThunk.rejected, (_state, _action) => {
        _state.initialFilterData = {
          isLoading: false,
        };
      })
      .addCase(fetchGraphApiThunk.pending, (_state, _action) => {
        _state.graphData = {
          isLoading: true,
        };
      })
      .addCase(fetchGraphApiThunk.fulfilled, (_state, _action) => {
        _state.graphData = {
          data: _action.payload?.data,
          isLoading: false,
        };
      })
      .addCase(fetchGraphApiThunk.rejected, (_state, _action) => {
        _state.graphData = {
          isLoading: false,
        };
      })
      .addCase(fetchDetailApiThunk.pending, (_state, _action) => ({
        ..._state,
        detailData: {
          ..._state.detailData,
          detailDataIsLoading: API_STATUS.STATUS_LOADING,
        },
      }))
      .addCase(fetchDetailApiThunk.fulfilled, (_state, _action) => {
        const { currentPage, limit } = _action?.payload?.paginationObj;
        let offset = (currentPage - 1) * limit;
        const detailViewData = _action?.payload?.data?.map((item, index) => {
          return { ...item, srNo: offset + index + 1 };
        });
        return {
          ..._state,
          detailData: {
            data: detailViewData,
            totalCount: _action?.payload?.data?.[0]?.totalcount,
            detailDataIsLoading: API_STATUS.STATUS_SUCCESS,
          },
        };
      })
      .addCase(fetchDetailApiThunk.rejected, (_state, _action) => ({
        ..._state,
        detailData: {
          detailDataIsLoading: API_STATUS.STATUS_FAILURE,
          data: [],
          message:
            _action.error.message || "Error while fetching email campaigns",
        },
      }));
  },
});

export const {
  setSelectedFilterData,
  setReplaceAllFilterData,
  setContextualFilterObj,
  updateSortParameters,
  updateExpendedMenu,
  setSelectedZones,
  setSelectedFilterObject,
  resetSelectedFilterData,
  resetClusterReducerState,
} = clusterSlice.actions;

export default clusterSlice.reducer;
